<template>
  <div class="page">
    <the-headersider />

    <div class="container">
      <div v-if="loading" class="loading-spinner">Loading...</div>
      <div v-else-if="error" class="error-message">{{ error }}</div>
      
      <form v-else class="form-grid" @submit.prevent="updateClient">
        <div>
          <label>Client Name</label>
          <input v-model="form.client_name" type="text" class="input" required />
        </div>

        <div>
          <label>Phone</label>
          <input v-model="form.phone" type="text" class="input" required />
        </div>

        <div>
          <label>Estate</label>
          <select v-model="form.Estate" class="input">
            <option disabled value="">Select estate</option>
            <option
              v-for="estate in estates"
              :key="estate.id"
              :value="estate.estateName"
            >
              {{ estate.estateName }}
            </option>
          </select>
        </div>

        <div>
          <label>Apartment</label>
          <select v-model="form.apartment" class="input">
            <option disabled value="">Select apartment</option>
            <option
              v-for="apartment in apartments"
              :key="apartment.id"
              :value="apartment.apartment_name"
            >
              {{ apartment.apartment_name }}
            </option>
          </select>
        </div>

        <div>
          <label>House No</label>
          <input
            v-model="form.house_number"
            type="text"
            class="input"
            placeholder="A12"
            required
          />
        </div>

        <div>
          <label>Monthly Payment</label>
          <input
            v-model.number="form.monthly_payment"
            type="number"
            class="input"
            min="0"
          />
        </div>

        <div>
          <label>Arrears</label>
          <input
            v-model.number="form.arrears"
            type="number"
            class="input"
            min="0"
          />
        </div>

        <div>
          <label>Billing Date</label>
          <input
            v-model="form.billing_date"
            type="date"
            class="input"
          />
        </div>

        <div class="active-box">
          <label>
            <input type="checkbox" v-model="form.isActive" />
            Active Client
          </label>
        </div>

        <div class="actions">
          <button type="submit" class="btn" :disabled="submitting">
            {{ submitting ? 'Updating...' : 'Update Client' }}
          </button>
          <button type="button" class="btn-cancel" @click="cancel">
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "EditClient",

  data() {
    return {
      estates: [],
      apartments: [],
      loading: false,
      submitting: false,
      error: null,
      form: {
        client_name: "",
        phone: "",
        billing_date: "",
        monthly_payment: 0,
        arrears: 0,
        Estate: "",       // Will store the estate name
        apartment: "",    // Will store the apartment name
        house_number: "",
        isActive: false
      }
    };
  },

  mounted() {
    this.loadEstates();
    this.loadApartments();
  },

  methods: {
    async loadEstates() {
      try {
        const res = await axios.get(
          "https://api.roberms.com/gla/get/estates"
        );
        this.estates = res.data;
        console.log("Estates loaded:", this.estates);
      } catch (error) {
        console.error("Error loading estates:", error);
        this.error = "Failed to load estates. Please try again.";
      }
    },

    async loadApartments() {
      try {
        const res = await axios.get(
          "https://api.roberms.com/gla/get/apartments"
        );
        this.apartments = res.data;
        console.log("Apartments loaded:", this.apartments);
        await this.loadClient();
      } catch (error) {
        console.error("Error loading apartments:", error);
        this.error = "Failed to load apartments. Please try again.";
      }
    },

    async loadClient() {
      this.loading = true;
      this.error = null;
      
      try {
        const res = await axios.get(
          `https://api.roberms.com/gla/client/${this.$route.params.clientId}`
        );

        const data = res.data;
        console.log("Client data from API:", data);
        
        this.form = {
          client_name: data.client_name || "",
          phone: data.phone || "",
          billing_date: data.billing_date || "",
          monthly_payment: data.monthly_payment || 0,
          arrears: data.arrears || 0,
          Estate: data.Estate || data.estateName || "", // Get the estate name
          apartment: data.apartment || data.apartment_name || "", // Get the apartment name
          house_number: data.house_number || "",
          isActive: data.isActive || false
        };

        console.log("Form populated with:", this.form);
        
      } catch (error) {
        console.error("Error loading client:", error);
        this.error = "Failed to load client data. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async updateClient() {
      // Validate form
      if (!this.form.client_name || !this.form.phone) {
        this.error = "Client name and phone are required.";
        return;
      }

      this.submitting = true;
      this.error = null;

      try {
        // Prepare data for backend - send the names
        const payload = {
          client_name: this.form.client_name,
          phone: this.form.phone,
          billing_date: this.form.billing_date,
          monthly_payment: this.form.monthly_payment,
          arrears: this.form.arrears,
          Estate: this.form.Estate,      // ← This is the estate name
          apartment: this.form.apartment, // ← This is the apartment name
          house_number: this.form.house_number,
          isActive: this.form.isActive
        };

        console.log("📤 Sending to backend:", payload);
        
        const response = await axios.put(
          `https://api.roberms.com/gla/edit/client/${this.$route.params.clientId}`,
          payload
        );
        
        console.log("✅ Backend response:", response.data);
        
        this.$router.replace("/clients");
      } catch (error) {
        console.error("❌ Error updating client:", error);
        this.error = "Failed to update client. Please try again.";
        this.submitting = false;
      }
    },

    cancel() {
      this.$router.push("/clients");
    }
  }
};
</script>

<style scoped>
.page {
  height: 100vh;
  overflow: hidden;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;
  align-items: end;
}

label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
  display: block;
}

.input {
  width: 100%;
  padding: 0.45rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.9rem;
  transition: border-color 0.3s;
}

.input:focus {
  outline: none;
  border-color: #2563eb;
}

.active-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.actions {
  grid-column: span 3;
  text-align: right;
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn {
  background: #2563eb;
  color: white;
  padding: 0.45rem 1.4rem;
  border-radius: 6px;
  font-weight: bold;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: background 0.3s;
}

.btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn:disabled {
  background: #93a9e0;
  cursor: not-allowed;
}

.btn-cancel {
  background: #dc2626;
  color: white;
  padding: 0.45rem 1.4rem;
  border-radius: 6px;
  font-weight: bold;
  font-size: 0.9rem;
  border: none;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-cancel:hover {
  background: #b91c1c;
}

.loading-spinner {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: #666;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #fecaca;
  margin-bottom: 1rem;
}
</style>